import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { configurarAvisoSessaoTrocada, sessaoDivergeDaAba } from '../../services/api';
import { AUTH_STORAGE_KEYS, lerSessaoAuth } from '../../services/authStorage';
import { MSG_SESSAO_TROCADA_ORIENTACAO, mensagemSessaoTrocada } from '../../services/avisoSessao';

const CHAVES_SESSAO = Object.values(AUTH_STORAGE_KEYS);

/** Recarregar a página: o /me traz a sessão atual do navegador e todo o estado da aba recomeça. */
const NAVEGACAO_PADRAO = {
  recarregar: () => window.location.reload(),
  irPara: (url) => window.location.assign(url),
};

/**
 * P-S10 (src/docs/sessao-trocada-entre-abas.md): o cookie da sessão é um só por navegador. Quando outra aba entra com
 * outro usuário (ou troca empresa/filial), ESTA aba fica bloqueada até recarregar — nada mais sai dela em nome da
 * sessão nova. Detecta de três jeitos:
 * - evento `storage` (outra aba gravou outra sessão no localStorage) — avisa antes de a pessoa tentar algo;
 * - recusa do back (409/403 SESSAO_TROCADA), via services/api.js;
 * - /me em segundo plano que volta com outra sessão (AuthContext.atualizarAcesso).
 * P-Q8: diz o motivo, o que fazer e dá a ação; não fecha sozinho nem com clique fora.
 */
export default function AvisoSessaoTrocada({ navegacao = NAVEGACAO_PADRAO }) {
  const { usuario, empresaId, filialId, logout } = useAuth();
  const [bloqueio, setBloqueio] = useState(null);
  // a mensagem compara com a sessão que a aba mostrava quando foi bloqueada
  const atualRef = useRef(null);
  atualRef.current = { usuario, empresaId, filialId };

  useEffect(() => {
    const bloquear = (info = {}) =>
      setBloqueio((anterior) => ({
        atual: anterior?.atual || atualRef.current,
        nova: info.sessaoNova || anterior?.nova || null,
      }));

    configurarAvisoSessaoTrocada(bloquear);

    const aoMudarArmazenamento = (evento) => {
      if (evento.key !== null && !CHAVES_SESSAO.includes(evento.key)) return;
      const salva = lerSessaoAuth();
      // saída na outra aba: o próximo pedido desta leva 401 e o fluxo de sessão expirada explica
      if (!salva.usuario) return;
      if (sessaoDivergeDaAba({ usuario: salva.usuario, empresaId: salva.empresaId, filialId: salva.filialId })) {
        bloquear({ sessaoNova: salva });
      }
    };
    window.addEventListener('storage', aoMudarArmazenamento);

    return () => {
      configurarAvisoSessaoTrocada(null);
      window.removeEventListener('storage', aoMudarArmazenamento);
    };
  }, []);

  const sair = () => {
    logout();
    navegacao.irPara('/login');
  };

  return (
    <Dialog open={Boolean(bloqueio)} disableEscapeKeyDown onClose={() => {}} data-testid="aviso-sessao-trocada" aria-labelledby="titulo-sessao-trocada">
      <DialogTitle id="titulo-sessao-trocada">Outra sessão entrou neste navegador</DialogTitle>
      <DialogContent>
        <DialogContentText data-testid="aviso-sessao-trocada-motivo" sx={{ mb: 1 }}>
          {bloqueio ? mensagemSessaoTrocada(bloqueio) : ''}
        </DialogContentText>
        <DialogContentText>{MSG_SESSAO_TROCADA_ORIENTACAO}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={sair} data-testid="aviso-sessao-trocada-sair">Sair</Button>
        <Button variant="contained" onClick={navegacao.recarregar} data-testid="aviso-sessao-trocada-recarregar" autoFocus>
          Recarregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
